import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, isAfter, isBefore, addDays, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box, Typography, Paper, Chip, CircularProgress,
  FormGroup, FormControlLabel, Checkbox, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Alert, Tooltip, Badge
} from '@mui/material';
import {
  CalendarMonth, Assignment, Event, Rocket,
  Close, OpenInNew, Add, FilterList,
  ChevronRight, AccessTime, Group, PriorityHigh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { calendarService, meetingService } from '../services/api';

// ── Localizer ──────────────────────────────────────────────────────
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { locale: fr }),
  getDay,
  locales: { fr },
});

const RBC_MESSAGES = {
  allDay: 'Journée',
  previous: '‹',
  next: '›',
  today: "Aujourd'hui",
  month: 'Mois',
  week: 'Semaine',
  day: 'Jour',
  agenda: 'Agenda',
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  noEventsInRange: 'Aucun événement dans cette période.',
  showMore: (n) => `+ ${n} de plus`,
};

// ── Constants ──────────────────────────────────────────────────────
const TYPE_CONFIG = {
  meeting: { label: 'Réunions',  color: '#9c27b0', icon: <Event sx={{ fontSize: 14 }} /> },
  task:    { label: 'Tâches',    color: '#2196f3', icon: <Assignment sx={{ fontSize: 14 }} /> },
  project: { label: 'Projets',   color: '#4caf50', icon: <Rocket sx={{ fontSize: 14 }} /> },
};

const STATUS_LABELS = {
  not_started: 'Non commencée', in_progress: 'En cours',
  completed: 'Terminée', blocked: 'Bloquée',
};
const PRIORITY_LABELS = { low: 'Faible', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente' };
const PRIORITY_COLORS = { low: '#22c55e', medium: '#f59e0b', high: '#f97316', urgent: '#ef4444' };

const fmt = (d) => d ? format(new Date(d), 'dd MMM yyyy', { locale: fr }) : '—';
const fmtTime = (d) => d ? format(new Date(d), 'dd MMM yyyy HH:mm', { locale: fr }) : '—';

// ── Component ──────────────────────────────────────────────────────
export default function CalendarPage() {
  const navigate = useNavigate();
  const [allEvents, setAllEvents]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [activeTypes, setActiveTypes]     = useState({ meeting: true, task: true, project: true });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [currentDate, setCurrentDate]     = useState(new Date());
  const [view, setView]                   = useState('month');

  // ── Fetch ──────────────────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await calendarService.getEvents();
      const events = res.data.map(e => ({
        ...e,
        start: new Date(e.start),
        end: new Date(e.end),
      }));
      setAllEvents(events);
    } catch {
      setError('Impossible de charger les événements du calendrier.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── Filtered events ────────────────────────────────────────────
  const visibleEvents = allEvents.filter(e => activeTypes[e.type]);

  // ── Upcoming events (next 14 days, max 8) ─────────────────────
  const now = new Date();
  const in14 = addDays(now, 14);
  const upcomingEvents = allEvents
    .filter(e => activeTypes[e.type] && isAfter(e.start, now) && isBefore(e.start, in14))
    .sort((a, b) => a.start - b.start)
    .slice(0, 8);

  // ── Handlers ──────────────────────────────────────────────────
  const toggleType = (type) => setActiveTypes(prev => ({ ...prev, [type]: !prev[type] }));

  const handleSelectEvent = (event) => setSelectedEvent(event);

  const handleMeetingCreated = () => {
    setMeetingDialogOpen(false);
    fetchEvents();
  };

  // ── Style getter ──────────────────────────────────────────────
  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: '4px',
      color: 'white',
      border: 'none',
      fontSize: '0.78rem',
      padding: '1px 4px',
      opacity: event.details?.status === 'completed' ? 0.6 : 0.9,
    },
  });

  // ── Detail dialog content ─────────────────────────────────────
  const renderEventDetail = () => {
    if (!selectedEvent) return null;
    const { type, details } = selectedEvent;

    if (type === 'meeting') {
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Event sx={{ color: '#9c27b0' }} />
            <Chip label="Réunion" size="small" sx={{ bgcolor: '#9c27b020', color: '#9c27b0', fontWeight: 600 }} />
          </Box>
          <DetailRow icon={<AccessTime />} label="Début"   value={fmtTime(details.start_time)} />
          <DetailRow icon={<AccessTime />} label="Fin"     value={fmtTime(details.end_time)} />
          {details.location && <DetailRow icon={<Group />} label="Lieu" value={details.location} />}
          {details.description && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">{details.description}</Typography>
            </Box>
          )}
          {details.meeting_link && (
            <Button
              variant="contained"
              startIcon={<OpenInNew />}
              href={details.meeting_link}
              target="_blank"
              sx={{ mt: 2 }}
              size="small"
            >
              Rejoindre la réunion
            </Button>
          )}
        </>
      );
    }

    if (type === 'task') {
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Assignment sx={{ color: details.color || '#2196f3' }} />
            <Chip label="Tâche" size="small" sx={{ bgcolor: '#2196f320', color: '#2196f3', fontWeight: 600 }} />
            <Chip
              label={STATUS_LABELS[details.status] || details.status}
              size="small"
              sx={{ bgcolor: '#64748b20', color: '#64748b', fontWeight: 600, ml: 'auto' }}
            />
          </Box>
          {details.project_title && (
            <DetailRow icon={<Rocket />} label="Projet" value={details.project_title} />
          )}
          {details.assignee_name && (
            <DetailRow icon={<Group />} label="Assigné à" value={details.assignee_name} />
          )}
          <DetailRow icon={<AccessTime />} label="Début" value={fmt(details.start_date)} />
          <DetailRow icon={<AccessTime />} label="Échéance" value={fmt(details.end_date)} />
          {details.priority && (
            <Box sx={{ mt: 1 }}>
              <Chip
                icon={<PriorityHigh sx={{ fontSize: '14px !important' }} />}
                label={`Priorité : ${PRIORITY_LABELS[details.priority] || details.priority}`}
                size="small"
                sx={{
                  bgcolor: (PRIORITY_COLORS[details.priority] || '#64748b') + '20',
                  color: PRIORITY_COLORS[details.priority] || '#64748b',
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
          {details.progress !== undefined && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Avancement : {details.progress}%</Typography>
              <Box sx={{ height: 6, bgcolor: 'action.hover', borderRadius: 3, mt: 0.5, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${details.progress}%`, bgcolor: 'primary.main', borderRadius: 3 }} />
              </Box>
            </Box>
          )}
          {details.project_id && (
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => { setSelectedEvent(null); navigate(`/projects/${details.project_id}`); }}
              sx={{ mt: 2 }}
              size="small"
            >
              Voir le projet
            </Button>
          )}
        </>
      );
    }

    if (type === 'project') {
      const total     = details.total_tasks || 0;
      const completed = details.completed_tasks || 0;
      const pct       = total > 0 ? Math.round((completed / total) * 100) : 0;
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Rocket sx={{ color: details.color || '#4caf50' }} />
            <Chip label="Projet" size="small" sx={{ bgcolor: '#4caf5020', color: '#4caf50', fontWeight: 600 }} />
          </Box>
          <DetailRow icon={<AccessTime />} label="Début"  value={fmt(details.start_date)} />
          <DetailRow icon={<AccessTime />} label="Fin"    value={fmt(details.end_date)} />
          <DetailRow icon={<Group />}      label="Membres" value={`${details.team_members || 0} membre(s)`} />
          <DetailRow icon={<Assignment />} label="Tâches" value={`${completed} / ${total} terminées`} />
          {total > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Progression : {pct}%</Typography>
              <Box sx={{ height: 6, bgcolor: 'action.hover', borderRadius: 3, mt: 0.5, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: details.color || '#4caf50', borderRadius: 3 }} />
              </Box>
            </Box>
          )}
          {details.id && (
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => { setSelectedEvent(null); navigate(`/projects/${details.id}`); }}
              sx={{ mt: 2 }}
              size="small"
            >
              Ouvrir le projet
            </Button>
          )}
        </>
      );
    }

    return null;
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', gap: 2, minHeight: '80vh' }}>

      {/* ── SIDEBAR ── */}
      <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* New meeting button */}
        <Button
          variant="contained"
          startIcon={<Add />}
          fullWidth
          onClick={() => setMeetingDialogOpen(true)}
          sx={{ borderRadius: 2, py: 1.2 }}
        >
          Nouvelle réunion
        </Button>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <FilterList fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight="bold">Filtres</Typography>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          <FormGroup>
            {Object.entries(TYPE_CONFIG).map(([type, cfg]) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={activeTypes[type]}
                    onChange={() => toggleType(type)}
                    size="small"
                    sx={{ color: cfg.color, '&.Mui-checked': { color: cfg.color } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
                    <Typography variant="body2">{cfg.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {allEvents.filter(e => e.type === type).length}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 0.5 }}
              />
            ))}
          </FormGroup>
        </Paper>

        {/* Upcoming events */}
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, flexGrow: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            Prochains événements
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : upcomingEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
              Aucun événement dans les 14 prochains jours
            </Typography>
          ) : (
            upcomingEvents.map((evt) => {
              const cfg = TYPE_CONFIG[evt.type] || TYPE_CONFIG.task;
              const isEvtToday = isToday(evt.start);
              return (
                <Box
                  key={evt.id}
                  onClick={() => setSelectedEvent(evt)}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
                    p: 1, borderRadius: 1.5, cursor: 'pointer',
                    bgcolor: isEvtToday ? `${cfg.color}15` : 'action.hover',
                    border: isEvtToday ? `1px solid ${cfg.color}40` : '1px solid transparent',
                    '&:hover': { bgcolor: `${cfg.color}20` },
                  }}
                >
                  <Box sx={{
                    width: 3, height: 36, borderRadius: 2, bgcolor: cfg.color,
                    flexShrink: 0, mt: 0.3,
                  }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight="bold" noWrap sx={{ display: 'block' }}>
                      {evt.title.replace(/^[📅✓🚀]\s*/, '')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(evt.start, isEvtToday ? "'Aujourd\'hui' HH:mm" : 'dd MMM', { locale: fr })}
                    </Typography>
                  </Box>
                  <ChevronRight sx={{ fontSize: 14, color: 'text.disabled', ml: 'auto', flexShrink: 0, mt: 0.5 }} />
                </Box>
              );
            })
          )}
        </Paper>

        {/* Legend */}
        <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold">LÉGENDE</Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {Object.entries(TYPE_CONFIG).map(([type, cfg]) => (
              <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: cfg.color, flexShrink: 0 }} />
                <Typography variant="caption">{cfg.label}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* ── MAIN CALENDAR ── */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', minHeight: 650 }}>
          {/* Header */}
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CalendarMonth color="primary" />
            <Typography variant="h6" fontWeight="bold">Calendrier global</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {Object.entries(activeTypes).filter(([, v]) => v).map(([t]) => (
                <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TYPE_CONFIG[t].color }} />
                </Box>
              ))}
              <Badge badgeContent={visibleEvents.length} color="primary" max={999}>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>événements</Typography>
              </Badge>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ p: 2, height: 'calc(100% - 64px)' }}>
              <Calendar
                localizer={localizer}
                events={visibleEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%', minHeight: 560 }}
                messages={RBC_MESSAGES}
                culture="fr"
                eventPropGetter={eventStyleGetter}
                views={['month', 'week', 'day', 'agenda']}
                view={view}
                onView={setView}
                date={currentDate}
                onNavigate={setCurrentDate}
                onSelectEvent={handleSelectEvent}
                popup
                tooltipAccessor={(e) => e.title.replace(/^[📅✓🚀]\s*/, '')}
              />
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── EVENT DETAIL DIALOG ── */}
      <Dialog
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ pr: 6, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold" noWrap>
                {selectedEvent.title.replace(/^[📅✓🚀]\s*/, '')}
              </Typography>
              <IconButton
                onClick={() => setSelectedEvent(null)}
                sx={{ position: 'absolute', top: 12, right: 12 }}
                size="small"
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
              {renderEventDetail()}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setSelectedEvent(null)} variant="outlined" size="small">
                Fermer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── NEW MEETING DIALOG ── */}
      <MeetingFormDialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        onCreated={handleMeetingCreated}
      />
    </Box>
  );
}

// ── Helper component: detail row ──────────────────────────────────
function DetailRow({ icon, label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>{label}</Typography>
      <Typography variant="body2" fontWeight="500">{value}</Typography>
    </Box>
  );
}

// ── Inline meeting creation dialog ────────────────────────────────
function MeetingFormDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '', description: '', start_time: '', end_time: '',
    location: '', type: 'online', meeting_link: ''
  });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.title || !form.start_time || !form.end_time) {
      setError('Titre, date de début et date de fin sont requis.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await meetingService.create(form);
      setForm({ title: '', description: '', start_time: '', end_time: '', location: '', type: 'online', meeting_link: '' });
      onCreated();
    } catch {
      setError('Erreur lors de la création de la réunion.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Event color="secondary" />
        <Typography fontWeight="bold">Nouvelle réunion</Typography>
        <IconButton onClick={onClose} sx={{ ml: 'auto' }} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[
            { name: 'title',        label: 'Titre *',           type: 'text' },
            { name: 'description',  label: 'Description',       type: 'text', multiline: true, rows: 2 },
            { name: 'start_time',   label: 'Début *',           type: 'datetime-local' },
            { name: 'end_time',     label: 'Fin *',             type: 'datetime-local' },
            { name: 'location',     label: 'Lieu',              type: 'text' },
            { name: 'meeting_link', label: 'Lien de réunion',   type: 'url' },
          ].map(f => (
            <Box key={f.name}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.multiline ? (
                <textarea
                  name={f.name}
                  value={form[f.name]}
                  onChange={handleChange}
                  rows={f.rows}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8,
                    border: '1px solid #e2e8f0', fontFamily: 'inherit',
                    fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box',
                    background: 'transparent', color: 'inherit'
                  }}
                />
              ) : (
                <input
                  type={f.type}
                  name={f.name}
                  value={form[f.name]}
                  onChange={handleChange}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8,
                    border: '1px solid #e2e8f0', fontFamily: 'inherit',
                    fontSize: '0.875rem', boxSizing: 'border-box',
                    background: 'transparent', color: 'inherit'
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? 'Création…' : 'Créer la réunion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
